/**
 * Expected Position Calculator for Rhyme Scheme Analysis
 *
 * Part of Scope 21: Per-Section-Type Rhyme Scheme Configuration
 * Calculates expected rhyme positions based on configured patterns
 * and applies scheme-aware opacity penalties.
 */

import type { RhymeSchemePattern } from '../../types/rhymeScheme'
import { STABILITY_SCALE } from './stabilityInterpreter'

/**
 * Get the expected distance to rhyme partner based on scheme pattern
 *
 * @param pattern - The rhyme scheme pattern (AABB, ABAB, etc.)
 * @param lineIndexInSection - 0-indexed line position within section
 * @returns Expected distance to rhyme partner, or null for FREE scheme
 *
 * @example
 * // AABB pattern (couplets)
 * getExpectedRhymePosition('AABB', 0) // => 1 (line 0 rhymes with line 1)
 * getExpectedRhymePosition('AABB', 1) // => 1 (line 1 rhymes with line 0)
 * getExpectedRhymePosition('AABB', 2) // => 1 (line 2 rhymes with line 3)
 *
 * @example
 * // ABAB pattern (alternating)
 * getExpectedRhymePosition('ABAB', 0) // => 2 (line 0 rhymes with line 2)
 * getExpectedRhymePosition('ABAB', 1) // => 2 (line 1 rhymes with line 3)
 */
export function getExpectedRhymePosition(
  pattern: RhymeSchemePattern,
  lineIndexInSection: number
): number | null {
  // FREE and CUSTOM patterns have no fixed expectations
  if (pattern === 'FREE' || pattern === 'CUSTOM') {
    return null
  }

  switch (pattern) {
    case 'AABB': {
      // Couplets: lines rhyme in pairs (0-1, 2-3, 4-5, etc.)
      return 1
    }

    case 'ABAB': {
      // Alternating: every other line rhymes (0-2, 1-3, 4-6, 5-7, etc.)
      return 2
    }

    case 'ABBA': {
      // Enclosed rhyme: first and last lines rhyme, middle lines rhyme
      // Pattern repeats every 4 lines
      const posInPattern = lineIndexInSection % 4
      if (posInPattern === 0 || posInPattern === 3) {
        // Lines 0 and 3 rhyme with each other (distance 3)
        return 3
      } else {
        // Lines 1 and 2 rhyme with each other (distance 1)
        return 1
      }
    }

    case 'AAAA': {
      // Monorhyme: every line rhymes with every other line
      // No single "expected" distance, but closest neighbor is 1
      return 1
    }

    default:
      return null
  }
}

/**
 * Calculate distance from expected rhyme position
 *
 * @param actualDistance - Actual line distance between rhyming words
 * @param expectedDistance - Expected distance based on scheme
 * @returns Absolute difference, or 0 if no expectation
 *
 * @example
 * // Perfect match to expectation
 * calculateDistanceFromExpected(2, 2) // => 0
 *
 * @example
 * // Off by one line
 * calculateDistanceFromExpected(3, 2) // => 1
 *
 * @example
 * // No expectation (FREE scheme)
 * calculateDistanceFromExpected(5, null) // => 0
 */
export function calculateDistanceFromExpected(
  actualDistance: number,
  expectedDistance: number | null
): number {
  if (expectedDistance === null) {
    return 0
  }
  return Math.abs(actualDistance - expectedDistance)
}

/**
 * Calculate scheme-aware opacity combining stability and scheme expectations
 *
 * Applies a penalty when rhymes are farther from their expected position,
 * but never goes below the minimum opacity defined in STABILITY_SCALE.
 *
 * @param actualDistance - Actual line distance between rhyming words
 * @param expectedDistance - Expected distance based on scheme (null for no expectation)
 * @param stabilityLevel - Stability level from rhyme type interpreter (1-5)
 * @returns Opacity value between STABILITY_SCALE.minOpacity and 1.0
 *
 * @example
 * // Perfect match to ABAB expectation, high stability
 * calculateSchemeAwareOpacity(2, 2, 5) // => ~1.0 (no penalty)
 *
 * @example
 * // Off by 2 lines from ABAB expectation, medium stability
 * calculateSchemeAwareOpacity(4, 2, 3) // => ~0.6 (stability penalty + distance penalty)
 *
 * @example
 * // FREE scheme (no expectation), low stability
 * calculateSchemeAwareOpacity(10, null, 2) // => ~0.4 (only stability penalty)
 */
export function calculateSchemeAwareOpacity(
  actualDistance: number,
  expectedDistance: number | null,
  stabilityLevel: number
): number {
  // Get stability config for this level (clamp to 1-10, default to 5)
  const clampedLevel = Math.max(1, Math.min(10, stabilityLevel || 5))
  const stabilityConfig = STABILITY_SCALE[clampedLevel] || STABILITY_SCALE[5]

  // Start with full opacity
  let opacity = 1.0

  // Calculate distance penalty from scheme expectation
  const deviation = calculateDistanceFromExpected(actualDistance, expectedDistance)

  if (deviation > 0) {
    // Apply distance penalty: reduce opacity by 10% per line of deviation
    // Maximum penalty is 50% (5+ lines off)
    const distancePenalty = Math.min(deviation * 0.1, 0.5)
    opacity = 1.0 - distancePenalty
  }

  // Enforce minimum opacity from STABILITY_SCALE for this level
  return Math.max(opacity, stabilityConfig.minOpacity)
}

/**
 * Parse custom rhyme scheme pattern and get expected position
 *
 * For CUSTOM patterns, analyzes the pattern string to find expected rhyme partners.
 *
 * @param customPattern - Custom pattern string (e.g., "ABABCC")
 * @param lineIndexInSection - 0-indexed line position within section
 * @returns Expected distance to closest rhyme partner, or null if no partner exists
 *
 * @example
 * // ABABCC pattern
 * parseCustomPattern('ABABCC', 0) // => 2 (A at 0 rhymes with A at 2)
 * parseCustomPattern('ABABCC', 4) // => 1 (C at 4 rhymes with C at 5)
 */
export function parseCustomPattern(
  customPattern: string,
  lineIndexInSection: number
): number | null {
  if (!customPattern || lineIndexInSection >= customPattern.length) {
    return null
  }

  const currentLetter = customPattern[lineIndexInSection]

  // Find all positions with the same letter
  const matchingPositions: number[] = []
  for (let i = 0; i < customPattern.length; i++) {
    if (customPattern[i] === currentLetter && i !== lineIndexInSection) {
      matchingPositions.push(i)
    }
  }

  if (matchingPositions.length === 0) {
    return null
  }

  // Return distance to closest matching position
  const distances = matchingPositions.map(pos => Math.abs(pos - lineIndexInSection))
  return Math.min(...distances)
}

/**
 * Get expected rhyme position for any pattern including CUSTOM
 *
 * @param pattern - The rhyme scheme pattern
 * @param customPattern - Custom pattern string (only used if pattern === 'CUSTOM')
 * @param lineIndexInSection - 0-indexed line position within section
 * @returns Expected distance to rhyme partner, or null for no expectation
 */
export function getExpectedRhymePositionWithCustom(
  pattern: RhymeSchemePattern,
  customPattern: string | undefined,
  lineIndexInSection: number
): number | null {
  if (pattern === 'CUSTOM' && customPattern) {
    return parseCustomPattern(customPattern, lineIndexInSection)
  }

  return getExpectedRhymePosition(pattern, lineIndexInSection)
}
